'use client'

import * as stylex from '@stylexjs/stylex'
import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react'

import { breakpoints, colors, fonts, fontSizes, spacing } from '../tokens/tokens.stylex'
import { Heading, Text } from '../typography'

const styles = stylex.create({
  toolbar: {
    display: 'flex',
    flex: '1 1 auto',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.space8,
    minWidth: 0,
  },
  search: { flex: '1 1 200px', minWidth: 'min(200px, 100%)' },
  controls: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: spacing.space8, minWidth: 0 },
  actions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: spacing.space8, marginInlineStart: 'auto' },
  detail: {
    display: 'grid',
    gridTemplateColumns: { default: 'minmax(0, 1fr)', [breakpoints.wide]: 'minmax(0, 3fr) minmax(0, 1fr)' },
    gap: spacing.space24,
    alignItems: 'start',
  },
  detailWithoutProperties: { gridTemplateColumns: 'minmax(0, 1fr)' },
  main: { minWidth: 0 },
  properties: {
    minWidth: 0,
    borderBlockStartWidth: { default: 1, [breakpoints.wide]: 0 },
    borderBlockStartStyle: 'solid',
    borderBlockStartColor: colors.hairline,
    paddingBlockStart: { default: spacing.space16, [breakpoints.wide]: 0 },
    borderInlineStartWidth: { default: 0, [breakpoints.wide]: 1 },
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: colors.hairline,
    paddingInlineStart: { default: 0, [breakpoints.wide]: spacing.space24 },
  },
  section: { display: 'grid', gap: spacing.space16, minWidth: 0, fontFamily: fonts.sans, color: colors.foreground },
  sectionHeader: { display: 'grid', gap: spacing.space4 },
  description: { margin: 0, color: colors.muted, fontSize: fontSizes.small },
  sectionActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: spacing.space8,
  },
  row: {
    display: 'flex',
    fontFamily: fonts.sans,
    color: colors.foreground,
    alignItems: { default: 'center', [breakpoints.compact]: 'stretch' },
    flexDirection: { default: 'row', [breakpoints.compact]: 'column' },
    gap: { default: spacing.space24, [breakpoints.compact]: spacing.space8 },
    paddingBlock: spacing.space12,
    borderBlockEndWidth: 1,
    borderBlockEndStyle: 'solid',
    borderBlockEndColor: colors.hairline,
    minWidth: 0,
  },
  rowText: { flex: '1 1 55%', minWidth: 0 },
  label: { display: 'block', fontSize: fontSizes.small, fontWeight: 500 },
  rowDescription: { marginBlockStart: spacing.space4 },
  rowControl: { flex: '1 1 45%', minWidth: 0 },
})

type NativeLayoutProps = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'style' | 'color'>

type CollectionToolbarSlots = {
  navigation?: ReactNode
  search?: ReactNode
  filters?: ReactNode
  actions?: ReactNode
}

export type CollectionToolbarProps = NativeLayoutProps & CollectionToolbarSlots

/**
 * Group a collection's search, filters, and actions in a wrapping row. Choose Inline for ordinary action groups.
 * Applications supply named controls and own filtering, URL state, and results. Status: supported.
 */
export function CollectionToolbar({
  navigation,
  search,
  filters,
  actions,
  children,
  ...props
}: CollectionToolbarProps) {
  return (
    <div {...props} {...stylex.props(styles.toolbar)}>
      {navigation && <div {...stylex.props(styles.controls)}>{navigation}</div>}
      {search && <div {...stylex.props(styles.search)}>{search}</div>}
      {filters && <div {...stylex.props(styles.controls)}>{filters}</div>}
      {children}
      {actions && <div {...stylex.props(styles.actions)}>{actions}</div>}
    </div>
  )
}

type DetailLayoutSlots = {
  children: ReactNode
  properties?: ReactNode
  propertiesLabel?: string
}

export type DetailLayoutProps = NativeLayoutProps & DetailLayoutSlots

/**
 * Place record content beside a compact property rail that stacks on narrow screens. Choose Grid for equal peers.
 * Applications supply formatted properties and keep routing, record actions, and data ownership. Status: supported.
 */
export function DetailLayout({ children, properties, propertiesLabel = 'Properties', ...props }: DetailLayoutProps) {
  return (
    <div {...props} {...stylex.props(styles.detail, !properties && styles.detailWithoutProperties)}>
      <div {...stylex.props(styles.main)}>{children}</div>
      {properties && (
        <aside aria-label={propertiesLabel} {...stylex.props(styles.properties)}>
          {properties}
        </aside>
      )}
    </div>
  )
}

type SettingsSectionOptions = {
  title: string
  description?: string
  children: ReactNode
  actions?: ReactNode
}

export type SettingsSectionProps = Omit<
  ComponentPropsWithoutRef<'section'>,
  'className' | 'style' | 'color' | 'title'
> &
  SettingsSectionOptions

/**
 * Name a related group of settings and its supplied save actions. Choose Card when content needs a bounded surface.
 * Applications own form submission, validation, drafts, and pending or failure feedback. Status: supported.
 */
export function SettingsSection({ title, description, children, actions, ...props }: SettingsSectionProps) {
  const headingId = useId()

  return (
    <section {...props} aria-labelledby={headingId} {...stylex.props(styles.section)}>
      <header {...stylex.props(styles.sectionHeader)}>
        <Heading id={headingId} level={2} variant="section">
          {title}
        </Heading>
        {description && <p {...stylex.props(styles.description)}>{description}</p>}
      </header>
      <div>{children}</div>
      {actions && <div {...stylex.props(styles.sectionActions)}>{actions}</div>}
    </section>
  )
}

type SettingsRowOptions = {
  label: string
  description?: string
  descriptionId?: string
  htmlFor?: string | undefined
  children: ReactNode
}

export type SettingsRowProps = NativeLayoutProps & SettingsRowOptions

/**
 * Align one setting's label and description with its control. Choose Stack for a long or multi-field editor.
 * Applications associate the label with htmlFor and supply control IDs, descriptions, and validation. Status: supported.
 */
export function SettingsRow({ label, description, descriptionId, htmlFor, children, ...props }: SettingsRowProps) {
  return (
    <div {...props} {...stylex.props(styles.row)}>
      <div {...stylex.props(styles.rowText)}>
        {htmlFor ? (
          <label htmlFor={htmlFor} {...stylex.props(styles.label)}>
            {label}
          </label>
        ) : (
          <div {...stylex.props(styles.label)}>{label}</div>
        )}
        {description && (
          <div {...stylex.props(styles.rowDescription)}>
            <Text id={descriptionId} as="p" size="small" tone="muted">
              {description}
            </Text>
          </div>
        )}
      </div>
      <div {...stylex.props(styles.rowControl)}>{children}</div>
    </div>
  )
}
